import {useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject} from "react";
import {useNotifications} from "../../../lib/NotificationContext.tsx";
import {isAbortError} from "../../../lib/abortError.ts";
import {DEFAULT_CURRENCY} from "../../../types/constants.ts";
import type {CurrencyEnum, LegalEntityType} from "../../../generated/enums.ts";
import {
    allClosedNodes,
    allOpenNodes,
    defaultOpenNodes,
    getStoredPrefs,
    inclusiveMonthCount,
    isBeyondDefault,
    projectRows,
    resolveOpenNodes,
    storePrefs,
    type TrialBalancePrefs,
} from "./utils";
import type {ProjectableRow} from "./utils/viewProjection.ts";

export type FetchPeriodicStatement<TReport> = (
    startDate: string,
    endDate: string,
    currency?: CurrencyEnum,
    showPredictedAccounts?: boolean,
    signal?: AbortSignal,
) => Promise<TReport>;

export interface PeriodicStatementCopy {
    loadFailedTitle: string;
    refreshedTitle: string;
    refreshFailedDescription: string;
    logName: string;
}

interface UsePeriodicStatementInput<TReport> {
    userId: string;
    businessId: string;
    legalEntityType: LegalEntityType;
    fetchReport: FetchPeriodicStatement<TReport>;
    refreshTrialBalanceCache?: () => Promise<{invalidated_count: number}>;
    copy: PeriodicStatementCopy;
}

function isStaleRequest(
    seq: number,
    requestSeqRef: MutableRefObject<number>,
    signal?: AbortSignal,
): boolean {
    return signal?.aborted === true || seq !== requestSeqRef.current;
}

export function usePeriodicStatement<
    TRow extends ProjectableRow,
    TReport extends {rows: TRow[]; available_currencies: CurrencyEnum[] | null},
>({
    userId,
    businessId,
    legalEntityType,
    fetchReport,
    refreshTrialBalanceCache,
    copy,
}: UsePeriodicStatementInput<TReport>) {
    const [initialPrefs] = useState<TrialBalancePrefs>(() => getStoredPrefs(userId, businessId));
    const [dateFrom, setDateFrom] = useState<string>(initialPrefs.dateRange.from);
    const [dateTo, setDateTo] = useState<string>(initialPrefs.dateRange.to);
    const [selectedCurrency, setSelectedCurrency] = useState<CurrencyEnum>(DEFAULT_CURRENCY);
    const [showPredictedAccounts, setShowPredictedAccounts] = useState<boolean>(
        initialPrefs.predictedAccounts,
    );
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshingCache, setIsRefreshingCache] = useState(false);
    const [reportData, setReportData] = useState<TReport | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [openOverrides, setOpenOverrides] = useState<Record<string, boolean>>({});
    const {dispatch: dispatchNotification} = useNotifications();

    useEffect(() => {
        const stored = getStoredPrefs(userId, businessId);
        setDateFrom(stored.dateRange.from);
        setDateTo(stored.dateRange.to);
        setSelectedCurrency(DEFAULT_CURRENCY);
        setShowPredictedAccounts(stored.predictedAccounts);
        setOpenOverrides({});
        setSearchQuery("");
    }, [businessId, userId]);

    const requestSeqRef = useRef(0);

    const onLoad = useCallback(async (signal?: AbortSignal): Promise<boolean> => {
        if (!dateFrom || !dateTo) return false;

        const seq = ++requestSeqRef.current;
        setIsLoading(true);

        try {
            const report = await fetchReport(
                dateFrom,
                dateTo,
                selectedCurrency,
                showPredictedAccounts,
                signal,
            );
            if (isStaleRequest(seq, requestSeqRef, signal)) return false;

            setReportData(report);
            return true;
        } catch (error) {
            if (isStaleRequest(seq, requestSeqRef, signal) || isAbortError(error)) return false;

            console.error(`Failed to load ${copy.logName}:`, error);
            setReportData(null);
            dispatchNotification({
                title: copy.loadFailedTitle,
                description: "Kan de maandcijfers niet ophalen. Probeer het opnieuw.",
                type: "error",
            });
            return false;
        } finally {
            if (seq === requestSeqRef.current) setIsLoading(false);
        }
    }, [copy, dateFrom, dateTo, dispatchNotification, fetchReport, selectedCurrency, showPredictedAccounts]);

    async function refresh() {
        if (!refreshTrialBalanceCache || isRefreshingCache || isLoading) return;
        setIsRefreshingCache(true);
        try {
            await refreshTrialBalanceCache();
            const reloaded = await onLoad();
            if (!reloaded) return;
            dispatchNotification({
                title: copy.refreshedTitle,
                description: "De cijfers zijn opnieuw berekend.",
                type: "success",
            });
        } catch (error) {
            console.error(`${copy.logName} refresh failed:`, error);
            dispatchNotification({
                title: "Vernieuwen mislukt",
                description: copy.refreshFailedDescription,
                type: "error",
            });
        } finally {
            setIsRefreshingCache(false);
        }
    }

    useEffect(() => {
        if (!dateFrom || !dateTo) return;
        const controller = new AbortController();
        void onLoad(controller.signal);
        return () => controller.abort();
    }, [dateFrom, dateTo, selectedCurrency, showPredictedAccounts]);

    const rows = useMemo<TRow[]>(() => reportData?.rows ?? [], [reportData]);

    const skeletonMonthCount = useMemo(() => {
        const count = inclusiveMonthCount(dateFrom, dateTo);
        return count == null ? 12 : Math.max(1, count);
    }, [dateFrom, dateTo]);

    const currencyOptions = useMemo(() => {
        const fromApi = reportData?.available_currencies;
        return fromApi?.length ? fromApi : [DEFAULT_CURRENCY];
    }, [reportData?.available_currencies]);

    useEffect(() => {
        if (!currencyOptions.length) return;
        setSelectedCurrency((prev) =>
            currencyOptions.includes(prev) ? prev : currencyOptions[0],
        );
    }, [currencyOptions]);

    const defaultsMap = useMemo(() => defaultOpenNodes(rows), [rows]);
    const resolvedOpen = useMemo(() => resolveOpenNodes(rows, openOverrides), [rows, openOverrides]);
    const visibleRows = useMemo(
        () => projectRows({rows, openNodes: resolvedOpen, searchQuery, legalEntityType}),
        [rows, resolvedOpen, searchQuery, legalEntityType],
    );
    const beyondDefault = isBeyondDefault(resolvedOpen, rows);

    function persistPrefs(partial: Partial<TrialBalancePrefs>) {
        storePrefs(userId, businessId, {
            ...getStoredPrefs(userId, businessId),
            predictedAccounts: showPredictedAccounts,
            dateRange: {from: dateFrom, to: dateTo},
            ...partial,
        });
    }

    function handlePredictedChange(value: boolean) {
        setShowPredictedAccounts(value);
        persistPrefs({predictedAccounts: value});
    }

    function handleDateRangeChange(newFrom: string, newTo: string) {
        setDateFrom(newFrom);
        setDateTo(newTo);
        if (newFrom && newTo) {
            persistPrefs({dateRange: {from: newFrom, to: newTo}});
        }
    }

    function handleToggleNode(id: string) {
        if (searchQuery.trim()) return;
        setOpenOverrides((prev) => {
            const current = id in prev ? prev[id] : !!defaultsMap[id];
            return {...prev, [id]: !current};
        });
    }

    function handleToggleAll() {
        setOpenOverrides(beyondDefault ? allClosedNodes(rows) : allOpenNodes(rows));
    }

    return {
        dateFrom,
        dateTo,
        selectedCurrency,
        setSelectedCurrency,
        showPredictedAccounts,
        isLoading,
        isRefreshingCache,
        reportData,
        rows,
        searchQuery,
        setSearchQuery,
        visibleRows,
        beyondDefault,
        currencyOptions,
        skeletonMonthCount,
        refresh: refreshTrialBalanceCache ? refresh : undefined,
        handlePredictedChange,
        handleDateRangeChange,
        handleToggleNode,
        handleToggleAll,
    };
}
